import { useState, useCallback } from "react";
import { Trash, Upload, Download, FileText, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  { question: "Is deleting PDF pages free?", answer: "Yes, completely free with no signup required." },
  { question: "How do I select pages to delete?", answer: "Click on page thumbnails to mark them for deletion." },
  { question: "Can I undo deleted pages?", answer: "No, but your original file is unchanged. We create a new PDF without the selected pages." },
  { question: "Will deleting affect other pages?", answer: "No, remaining pages keep their original quality and content." },
  { question: "Are my files secure?", answer: "Yes, files auto-delete after 30 minutes." }
];

const PdfDeletePagesPage = () => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState(new Set());

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResult(null);
      await loadPreview(droppedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResult(null);
      await loadPreview(selectedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const loadPreview = async (pdfFile) => {
    setIsLoadingPreview(true);
    const formData = new FormData();
    formData.append("file", pdfFile);

    try {
      const response = await axios.post(`${API}/pdf-preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setPages(response.data.pages);
        setSelectedPages(new Set());
      }
    } catch (error) {
      toast.error("Failed to load preview");
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const togglePage = (pageNum) => {
    setSelectedPages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pageNum)) {
        newSet.delete(pageNum);
      } else {
        // Don't allow selecting all pages
        if (newSet.size < pages.length - 1) {
          newSet.add(pageNum);
        } else {
          toast.error("You must keep at least one page");
        }
      }
      return newSet;
    });
  };

  const handleDelete = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    if (selectedPages.size === 0) {
      toast.error("No pages selected for deletion");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("pages", Array.from(selectedPages).join(","));

    try {
      const response = await axios.post(`${API}/pdf-delete-pages`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        await downloadFile(`${API}/download/${result.files[0].id}`, result.files[0].name || "edited.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPages([]);
    setSelectedPages(new Set());
  };

  return (
    <>
      <SEOHead
        title="Delete PDF Pages Free Online | Remove Pages"
        description="Delete unwanted pages from PDF files. Select and remove pages visually. Free online PDF page remover. No signup required."
        canonicalPath="/pdf-delete-pages"
        keywords="delete PDF pages, remove PDF pages, PDF page remover free, remove pages from PDF online"
      />
      <div className="py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-black flex items-center justify-center">
                <Trash className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Delete PDF Pages Free Online
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Remove unwanted pages from your PDF. Click pages to mark them for deletion.
              No signup required.
            </p>
          </div>

          {/* Upload Area */}
          {!result && pages.length === 0 && (
            <div className="space-y-6 animate-fade-in-up animate-delay-100">
              <div
                className={`upload-zone ${isDragging ? "dragging" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-input").click()}
                data-testid="upload-zone"
              >
                <input
                id="file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {isLoadingPreview ? (
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-muted-foreground" />
                  <p className="font-medium">Loading preview...</p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse (max 50MB)
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Page Preview Grid */}
        {!result && pages.length > 0 && (
          <div className="space-y-6 animate-fade-in-up" data-testid="preview-section">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">{file?.name}</h2>
                <p className="text-sm text-muted-foreground">
                  {pages.length} pages • {selectedPages.size} selected for deletion
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedPages(new Set())}
                className="rounded-sm"
                data-testid="clear-selection"
              >
                Clear Selection
              </Button>
            </div>

            <p className="text-sm text-muted-foreground bg-secondary p-3">
              Click on pages to select them for deletion. Selected pages will be removed from the final PDF.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {pages.map((page) => (
                <div
                  key={page.page}
                  onClick={() => togglePage(page.page)}
                  className={`border-2 transition-all p-3 cursor-pointer relative ${
                    selectedPages.has(page.page)
                      ? "border-[#FF3B30] bg-red-50"
                      : "border-border hover:border-black"
                  }`}
                  data-testid={`page-${page.page}`}
                >
                  {selectedPages.has(page.page) && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-[#FF3B30] flex items-center justify-center">
                      <X className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div className={`aspect-[3/4] bg-secondary flex items-center justify-center mb-3 overflow-hidden ${
                    selectedPages.has(page.page) ? "opacity-50" : ""
                  }`}>
                    <img
                      src={`${API}/download/${page.thumbnail}`}
                      alt={`Page ${page.page}`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-mono">Page {page.page}</span>
                    {selectedPages.has(page.page) && (
                      <span className="text-xs text-[#FF3B30] font-medium">DELETE</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleReset}
                className="flex-1 h-12 rounded-sm"
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={selectedPages.size === 0 || isProcessing}
                className="flex-1 h-12 bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white rounded-sm btn-hover-lift"
                data-testid="delete-button"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash className="w-5 h-5 mr-2" />
                    Delete {selectedPages.size} Page{selectedPages.size !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8 text-center">
              <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">
                Pages Deleted Successfully
              </h2>
              <p className="text-muted-foreground mb-6">
                {result.message}
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-sm"
                  data-testid="reset-button"
                >
                  <Trash className="w-4 h-4 mr-2" />
                  Edit Another PDF
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                  data-testid="download-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>

            <div className="bg-secondary p-4 text-center">
              <p className="text-sm text-muted-foreground">
                ⏱ File will be automatically deleted in 30 minutes
              </p>
            </div>
          </div>
        )}

        <ToolContentSection toolKey="pdf-delete-pages" content={toolContent["pdf-delete-pages"]} />
        <FAQSection faqs={faqs} />
        <RelatedTools currentTool="pdf-delete-pages" />
      </div>
    </div>
    </>
  );
};

export default PdfDeletePagesPage;
